import { styled } from '@mui/material';
import NoRubrics from 'components/Rubrics/NoRubrics';
import { getRubricTemplateTotalPoints } from 'components/Rubrics/Table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    Button,
    Container,
    Icon,
    IconButton,
    Link,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    ScrollableTableSortableHeaderCells,
    Stack,
    Text,
    TextInput,
} from 'shell/components';
import { roundTo } from 'utils/helper';

const Tablet = ({
    rubrics,
    filters,
    setFilters,
    getFilterCount,
    onClickEdit,
    onClickDownloadCSV,
    onClickDelete,
    sortState,
    handleSetSortState,
    columns,
}) => {
    const { t } = useTranslation('assignment');
    const history = useHistory();

    return (
        <CustomContainer device={'tablet'}>
            <CustomStack spacing={5}>
                <Stack spacing={4}>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {t('rubric.page.title', 'Rubric Templates')}
                    </Text>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                        <CustomButton
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            variant={'secondary'}
                            onClick={() => setFilters((current) => ({ ...current, open: true }))}
                        >
                            {t('common:controls.filters', 'Filter')} ({getFilterCount(filters, sortState)})
                        </CustomButton>
                        <Stack alignItems={'center'} direction={'row'} spacing={4}>
                            <TextInput
                                aria-label={'search'}
                                endIcon={<Icon kind={'search'} />}
                                placeholder={t('common:controls.search', 'Search')}
                                value={filters.search}
                                onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                            />
                            <CustomButton
                                startIcon={<Icon kind={'add'} />}
                                onClick={() => history.push('/rubrics/get-started')}
                            >
                                {t('rubric.page.new-rubric', 'New Rubric')}
                            </CustomButton>
                        </Stack>
                    </Stack>
                </Stack>
                {rubrics.length ? (
                    <CustomScrollableTable aria-label={'list of rubrics'}>
                        <ScrollableTableHead>
                            <ScrollableTableRow>
                                <ScrollableTableSortableHeaderCells
                                    columns={columns}
                                    sortState={sortState}
                                    handleSetSortState={handleSetSortState}
                                />

                                <CustomScrollableTableCell>
                                    {t('common:table.actions', 'Actions')}
                                </CustomScrollableTableCell>
                            </ScrollableTableRow>
                        </ScrollableTableHead>

                        <ScrollableTableBody>
                            {rubrics.map((rubric) => (
                                <ScrollableTableRow key={rubric.id}>
                                    <CustomScrollableTableCell>
                                        <Link to={`/rubrics/${rubric.id}`}>{rubric.rubric.title}</Link>
                                    </CustomScrollableTableCell>
                                    <CustomScrollableTableCell>
                                        {t('common:ui.format-date-time', { val: rubric.modified_on })}
                                    </CustomScrollableTableCell>
                                    <CustomScrollableTableCell>
                                        {rubric.rubric.criteria.length}
                                    </CustomScrollableTableCell>
                                    <CustomScrollableTableCell>
                                        {getRubricTemplateTotalPoints(rubric.rubric) === null
                                            ? t('common:status.unscored', 'Unscored')
                                            : roundTo(getRubricTemplateTotalPoints(rubric.rubric))}
                                    </CustomScrollableTableCell>
                                    <CustomScrollableTableCell>
                                        <Stack direction={'row'} spacing={2}>
                                            <IconButton
                                                aria-label={'edit rubric'}
                                                kind={'edit'}
                                                size={'small'}
                                                tooltip={{ component: t('common:controls.edit', 'Edit') }}
                                                variant={'square'}
                                                onClick={() => onClickEdit(rubric)}
                                            />
                                            <IconButton
                                                aria-label={'download rubric'}
                                                kind={'download'}
                                                size={'small'}
                                                tooltip={{
                                                    component: t('common:controls.download-csv', 'Download CSV'),
                                                }}
                                                variant={'square'}
                                                onClick={() => onClickDownloadCSV(rubric)}
                                            />
                                            <IconButton
                                                aria-label={'delete rubric'}
                                                kind={'delete'}
                                                size={'small'}
                                                tooltip={{ component: t('common:controls.delete', 'Delete') }}
                                                variant={'square'}
                                                onClick={() => onClickDelete(rubric)}
                                            />
                                        </Stack>
                                    </CustomScrollableTableCell>
                                </ScrollableTableRow>
                            ))}
                        </ScrollableTableBody>
                    </CustomScrollableTable>
                ) : (
                    <NoRubrics />
                )}
            </CustomStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(() => {
    return {
        height: '100%',
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        width: '100%',
        minHeight: '100%',
    };
});

const CustomButton = styled(Button)(() => {
    return {
        flexShrink: 0,
    };
});

const CustomScrollableTable = styled(ScrollableTable)(() => {
    return {
        flex: 1,
    };
});

const CustomScrollableTableCell = styled(ScrollableTableCell)(() => {
    return {
        '&:nth-of-type(1)': {
            width: '40%',
        },
        '&:nth-of-type(2)': {
            width: '25%',
        },
        '&:nth-of-type(3)': {
            width: '12.5%',
        },
        '&:nth-of-type(4)': {
            width: '12.5%',
        },
        '&:nth-of-type(5)': {
            width: '10%',
        },
    };
});

export default Tablet;
